/**
 * Configuración → Lados de máquinas (`/configuracion/lados`).
 *
 * Puerto de `Maquina_lado` + `Maquina_lado_model` + `views/menu/maquina_lado/*`.
 * Un **lado** es la cara surtidora de una máquina: `controllerPump` es el número
 * con el que aparece en el controlador PTS (y lo que usa Monitoreo en vivo para
 * identificar cada surtidor); `0` significa "sin posición en el controlador".
 * Las **mangueras** del lado (`maquina_producto`) son los productos que despacha.
 *
 * Reglas conservadas: el alta fija el estado y la fecha, el nombre es obligatorio
 * y la foto es opcional (`images/maquinas_lado`). El panel anterior mostraba "En
 * uso por" según el operador que el controlador tiene asignado en ese momento.
 *
 * Tres diferencias anotadas:
 * - El panel anterior ocultaba los lados desactivados (`estado_maquina_lado = 1`)
 *   y no había forma de reactivarlos. Aquí se listan todos con su estado.
 * - Se valida el **número de controlador repetido** (409): `pts_nozzles.pumpNozzle`
 *   es clave primaria y dos lados con el mismo número quedaban indistinguibles al
 *   leer el controlador. El panel anterior no validaba nada.
 * - Al asignar mangueras, el panel anterior creaba una fila por **todos** los
 *   productos del formulario (los marcados y los no marcados). Aquí sólo se
 *   guardan los marcados y los que se destildan quedan **desactivados** (no se
 *   borran, para no perder el contador acumulado).
 */

import {
  ArrowLeft,
  Droplets,
  Image as ImageIcon,
  MoveHorizontal,
  Pencil,
  Plus,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import {
  urlFotoLado,
  useLados,
  useManguerasLado,
  useMutarLados,
  useMutarManguerasLado,
} from '../avanzado.api';
import type { Lado, LadoPayload, MangueraLado, MaquinaOpcion } from '../avanzado.types';
import { FotoDialogo } from '../components/foto-dialogo';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Posiciones del controlador que ofrece el panel (0 = sin posición). */
const POSICIONES = Array.from({ length: 9 }, (_, indice) => indice);

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Miniatura de la foto del lado (casi ninguno tiene archivo en disco). */
function Miniatura({ lado }: { lado: Lado }) {
  const [fallo, setFallo] = useState(false);

  if (!lado.foto || fallo) {
    return (
      <span
        aria-hidden="true"
        className="flex size-8 items-center justify-center rounded-control bg-ink-100 text-ink-400"
      >
        <ImageIcon className="size-4" />
      </span>
    );
  }

  return (
    <img
      src={urlFotoLado(lado.id, lado.foto)}
      alt={`Foto de ${lado.nombre}`}
      className="size-8 rounded-control object-cover ring-1 ring-ink-100"
      onError={() => setFallo(true)}
    />
  );
}

/** Alta/edición de un lado. */
function LadoDialogo({
  lado,
  maquinas,
  ocupados,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  lado: Lado | null;
  maquinas: MaquinaOpcion[];
  /** Números de controlador ya usados, con el lado que los tiene tomados. */
  ocupados: Map<number, { id: number; nombre: string }>;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: LadoPayload) => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(lado?.nombre ?? '');
  const [maquinaId, setMaquinaId] = useState<number>(lado?.maquina_id ?? maquinas[0]?.id ?? 0);
  const [controlador, setControlador] = useState<number>(lado?.controlador ?? 0);
  const [activo, setActivo] = useState(lado?.activo ?? true);

  const valido = nombre.trim().length > 0 && maquinaId > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={lado ? `Editar lado ${lado.nombre}` : 'Nuevo lado'}
      description="El número de controlador es la posición del lado en el controlador PTS (0 si no tiene)."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({
                nombre: nombre.trim(),
                maquina_id: maquinaId,
                controlador,
                ...(lado ? { activo } : {}),
              })
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <Input
          label="Nombre del lado"
          value={nombre}
          maxLength={400}
          autoFocus
          placeholder="Ej. LADO 1"
          onChange={(evento) => setNombre(evento.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="lado-maquina" className="text-sm font-medium text-ink-700">
              Máquina
            </label>
            <Select
              id="lado-maquina"
              className={CONTROL}
              value={String(maquinaId)}
              onChange={(evento) => setMaquinaId(Number(evento.target.value))}
            >
              {maquinas.map((maquina) => (
                <option key={maquina.id} value={maquina.id}>
                  {maquina.nombre}
                  {maquina.activo ? '' : ' (inactiva)'}
                  {maquina.tipo === 2 ? ' · bodega' : ''}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="lado-controlador" className="text-sm font-medium text-ink-700">
              Nº de controlador
            </label>
            <Select
              id="lado-controlador"
              className={CONTROL}
              value={String(controlador)}
              onChange={(evento) => setControlador(Number(evento.target.value))}
            >
              {POSICIONES.map((numero) => {
                const dueno = ocupados.get(numero);
                const bloqueado = numero > 0 && dueno !== undefined && lado?.id !== dueno.id;

                return (
                  <option key={numero} value={numero} disabled={bloqueado}>
                    {numero === 0 ? '0 · Sin posición en el controlador' : `Lado ${numero}`}
                    {bloqueado ? ` (usado por ${dueno?.nombre})` : ''}
                  </option>
                );
              })}
            </Select>
          </div>
        </div>

        {lado ? (
          <Checkbox
            label="Lado activo"
            hint="Un lado desactivado no aparece en los selectores ni en los informes"
            checked={activo}
            onChange={(evento) => setActivo(evento.target.checked)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

/** Formulario de mangueras ya cargado (se monta cuando llega la respuesta). */
function ManguerasForm({
  lado,
  productos,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  lado: Lado;
  productos: MangueraLado[];
  guardando: boolean;
  error: string | null;
  onGuardar: (productos: { producto_id: number; contador: number; activo: boolean }[]) => void;
  onCerrar: () => void;
}) {
  const [filas, setFilas] = useState(() =>
    productos.map((producto) => ({
      producto_id: producto.producto_id,
      activo: producto.activo,
      contador: String(producto.contador),
      nombre: producto.nombre,
      color: producto.color,
      tipo: producto.tipo,
    })),
  );

  const marcados = filas.filter((fila) => fila.activo).length;

  const alternar = (productoId: number, activo: boolean) => {
    setFilas((actuales) =>
      actuales.map((fila) => (fila.producto_id === productoId ? { ...fila, activo } : fila)),
    );
  };

  const cambiarContador = (productoId: number, contador: string) => {
    setFilas((actuales) =>
      actuales.map((fila) => (fila.producto_id === productoId ? { ...fila, contador } : fila)),
    );
  };

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={`Mangueras de ${lado.nombre}`}
      description="Marca los productos que despacha el lado. El contador es el valor inicial del acumulado de esa manguera."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            onClick={() =>
              onGuardar(
                filas
                  .filter((fila) => fila.activo)
                  .map((fila) => ({
                    producto_id: fila.producto_id,
                    contador: Number(fila.contador.replace(',', '.')) || 0,
                    activo: true,
                  })),
              )
            }
          >
            Guardar mangueras
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        {lado.tipo_maquina === 2 ? (
          <Alert variant="warning">
            «{lado.maquina}» es una máquina <strong>sin contadores</strong> (bodega): las mangueras
            aquí son sólo para saber qué productos agrupa el lado.
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
          <Badge variant="neutral">{marcados} de {filas.length} marcadas</Badge>
          <span>El contador queda en 0 si lo dejas vacío.</span>
        </div>

        <div className="max-h-80 overflow-y-auto rounded-control border border-ink-100">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-ink-50/95 backdrop-blur">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                <th className="w-12 px-3 py-2">Usa</th>
                <th className="px-3 py-2">Producto</th>
                <th className="w-32 px-3 py-2">Contador</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((fila) => (
                <tr key={fila.producto_id} className="border-t border-ink-100">
                  <td className="px-3 py-2">
                    <Checkbox
                      label=""
                      aria-label={`Usar ${fila.nombre}`}
                      checked={fila.activo}
                      onChange={(evento) => alternar(fila.producto_id, evento.target.checked)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <span className="flex flex-wrap items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="size-3 shrink-0 rounded-full ring-1 ring-ink-200"
                        style={{ backgroundColor: fila.color ?? '#e5e7eb' }}
                      />
                      <span className="font-medium text-ink-800">{fila.nombre}</span>
                      {fila.tipo ? (
                        <Badge variant={fila.tipo === 'COMBUSTIBLE' ? 'brand' : 'outline'}>
                          {fila.tipo === 'COMBUSTIBLE' ? 'Combustible' : fila.tipo}
                        </Badge>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      disabled={!fila.activo}
                      aria-label={`Contador de ${fila.nombre}`}
                      className={cn(
                        'h-9 w-full rounded-control border border-ink-200 px-2 text-sm text-ink-800',
                        'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
                        'disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-400',
                      )}
                      value={fila.contador}
                      onChange={(evento) => cambiarContador(fila.producto_id, evento.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-ink-400">
          Los productos que destildes quedan <strong>desactivados</strong> (no se borran, así no se
          pierde el contador acumulado).
        </p>
      </div>
    </Dialog>
  );
}

/** Diálogo de mangueras del lado (carga los productos antes de montar el formulario). */
function ManguerasDialogo({ lado, onCerrar }: { lado: Lado; onCerrar: () => void }) {
  const consulta = useManguerasLado(lado.id);
  const guardar = useMutarManguerasLado(lado.id);
  const [error, setError] = useState<string | null>(null);

  const cargando = consulta.isLoading;

  return (
    <>
      {cargando ? (
        <Dialog open onClose={onCerrar} title={`Mangueras de ${lado.nombre}`}>
          <Skeleton className="h-64 w-full" />
        </Dialog>
      ) : consulta.error ? (
        <Dialog
          open
          onClose={onCerrar}
          title={`Mangueras de ${lado.nombre}`}
          footer={
            <Button variant="ghost" size="sm" onClick={onCerrar}>
              Cerrar
            </Button>
          }
        >
          <Alert variant="error" title="No se pudieron leer las mangueras">
            {mensajeDe(consulta.error, 'La API no respondió.')}
          </Alert>
        </Dialog>
      ) : (
        <ManguerasForm
          key={`mangueras-${lado.id}`}
          lado={consulta.data?.lado ?? lado}
          productos={consulta.data?.productos ?? []}
          guardando={guardar.isPending}
          error={error}
          onGuardar={(productos) => {
            setError(null);
            guardar.mutate(productos, {
              onSuccess: () => onCerrar(),
              onError: (fallo) => setError(mensajeDe(fallo, 'No se pudieron guardar las mangueras.')),
            });
          }}
          onCerrar={onCerrar}
        />
      )}
    </>
  );
}

export function LadosPage() {
  useDocumentTitle('Configuración · Lados de máquinas');

  const lados = useLados();
  const mutaciones = useMutarLados();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; lado: Lado | null }>({
    abierto: false,
    lado: null,
  });
  const [mangueras, setMangueras] = useState<Lado | null>(null);
  const [foto, setFoto] = useState<Lado | null>(null);
  const [porCambiar, setPorCambiar] = useState<Lado | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [errorFoto, setErrorFoto] = useState<string | null>(null);

  const sinPermiso = lados.error instanceof ApiError && lados.error.status === 403;
  const lista = lados.data?.lados ?? [];
  const maquinas = lados.data?.maquinas ?? [];

  /** Número de controlador → lado que lo tiene tomado. */
  const ocupados = new Map<number, { id: number; nombre: string }>();
  for (const lado of lista) {
    if (lado.controlador > 0) ocupados.set(lado.controlador, { id: lado.id, nombre: lado.nombre });
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <MoveHorizontal className="size-6 text-brand-500" aria-hidden="true" />
          Lados de máquinas
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Cada lado es una cara del surtidor. El número de controlador lo enlaza con el controlador
          PTS y con lo que muestra <strong>Monitoreo en vivo</strong>.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso || maquinas.length === 0}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, lado: null });
        }}
      >
        Nuevo lado
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: LadoPayload) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, lado: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar el lado.')),
    };

    if (dialogo.lado) {
      mutaciones.actualizar.mutate({ id: dialogo.lado.id, payload }, alGuardar);
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const cambiarEstado = () => {
    if (!porCambiar) return;

    mutaciones.cambiarEstado.mutate(
      { id: porCambiar.id, activo: !porCambiar.activo },
      {
        onSuccess: (datos) => {
          setPorCambiar(null);
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) => {
          setPorCambiar(null);
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {lados.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : lados.error ? (
        <Alert variant="error" title="No se pudieron leer los lados">
          {mensajeDe(lados.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Lista de lados</CardTitle>
            <Badge variant="neutral">
              {lados.data?.activos ?? 0} activos de {lados.data?.total ?? 0}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[60rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="w-12 pb-2">Foto</th>
                    <th className="pb-2">Lado</th>
                    <th className="w-40 pb-2">Máquina</th>
                    <th className="w-28 pb-2 text-center">Controlador</th>
                    <th className="w-28 pb-2 text-center">Mangueras</th>
                    <th className="w-44 pb-2">En uso por</th>
                    <th className="w-24 pb-2">Estado</th>
                    <th className="w-48 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((lado) => (
                    <tr key={lado.id} className="border-t border-ink-100">
                      <td className="py-3">
                        <Miniatura lado={lado} />
                      </td>
                      <td className="py-3 font-medium text-ink-900">{lado.nombre}</td>
                      <td className="py-3 text-ink-700">
                        {lado.maquina || '—'}
                        {lado.tipo_maquina === 2 ? (
                          <Badge variant="outline" className="ml-2">
                            Bodega
                          </Badge>
                        ) : null}
                      </td>
                      <td className="py-3 text-center">
                        {lado.controlador > 0 ? (
                          <Badge variant="brand">Lado {lado.controlador}</Badge>
                        ) : (
                          <span className="text-xs text-ink-400">sin posición</span>
                        )}
                      </td>
                      <td className="py-3 text-center">
                        <Badge variant={lado.mangueras > 0 ? 'neutral' : 'outline'}>
                          {formatNumber(lado.mangueras)}
                          {lado.mangueras_configuradas > lado.mangueras
                            ? ` (+${lado.mangueras_configuradas - lado.mangueras})`
                            : ''}
                        </Badge>
                      </td>
                      <td className="py-3 text-xs text-ink-600">
                        {lado.en_uso_por ?? <span className="text-ink-400">libre</span>}
                      </td>
                      <td className="py-3">
                        <Badge variant={lado.activo ? 'success' : 'neutral'}>
                          {lado.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-brand-600"
                            leftIcon={<Droplets className="size-3.5" aria-hidden="true" />}
                            disabled={!lado.activo}
                            title={lado.activo ? 'Asignar mangueras' : 'Activa el lado para configurarlo'}
                            onClick={() => setMangueras(lado)}
                          >
                            Mangueras
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Foto de ${lado.nombre}`}
                            title="Cambiar foto"
                            onClick={() => {
                              setErrorFoto(null);
                              setFoto(lado);
                            }}
                          >
                            <ImageIcon className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${lado.nombre}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, lado });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn('text-xs', lado.activo ? 'text-ink-500' : 'text-brand-600')}
                            onClick={() => setPorCambiar(lado)}
                          >
                            {lado.activo ? 'Desactivar' : 'Activar'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Alert variant="info" className="mt-4">
              Un lado <strong>no se elimina</strong>: los contómetros y las ventas apuntan a él. El
              número de controlador no se puede repetir entre lados.
            </Alert>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <LadoDialogo
          key={`lado-${dialogo.lado?.id ?? 'nuevo'}`}
          lado={dialogo.lado}
          maquinas={maquinas}
          ocupados={ocupados}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, lado: null })}
        />
      ) : null}

      {mangueras ? (
        <ManguerasDialogo
          key={`mangueras-${mangueras.id}`}
          lado={mangueras}
          onCerrar={() => setMangueras(null)}
        />
      ) : null}

      {foto ? (
        <FotoDialogo
          key={`foto-${foto.id}`}
          titulo={`Foto de ${foto.nombre}`}
          descripcion="Opcional. Se guarda en images/maquinas_lado y reemplaza la anterior."
          urlActual={foto.foto ? urlFotoLado(foto.id, foto.foto) : null}
          altActual={foto.nombre}
          subiendo={mutaciones.subirFoto.isPending}
          error={errorFoto}
          onSubir={(archivo) => {
            setErrorFoto(null);
            mutaciones.subirFoto.mutate(
              { id: foto.id, archivo },
              {
                onSuccess: () => {
                  setFoto(null);
                  setAviso({ tono: 'success', texto: 'Foto del lado actualizada.' });
                },
                onError: (error) => setErrorFoto(mensajeDe(error, 'No se pudo subir la foto.')),
              },
            );
          }}
          onCerrar={() => setFoto(null)}
        />
      ) : null}

      <ConfirmDialog
        open={porCambiar !== null}
        title={porCambiar?.activo ? '¿Desactivar el lado?' : '¿Activar el lado?'}
        description={
          porCambiar?.activo
            ? `«${porCambiar?.nombre ?? ''}» dejará de aparecer en los selectores y en los informes. Sus mangueras y contadores se conservan.`
            : `«${porCambiar?.nombre ?? ''}» volverá a estar disponible en el sistema.`
        }
        confirmLabel={porCambiar?.activo ? 'Sí, desactivar' : 'Sí, activar'}
        loading={mutaciones.cambiarEstado.isPending}
        onClose={() => setPorCambiar(null)}
        onConfirm={cambiarEstado}
      />
    </div>
  );
}
